import { useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { UserAvatar } from "./UserAvatar";
import { api, extractAxiosError } from "../lib/api";
import { resolveAvatarUrl } from "../lib/avatar";

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;

export function AvatarUpload({
    name,
    avatarUrl,
    onUploaded,
    uploadUrl,
    removeUrl,
    label = "Profile photo",
}) {
    const inputRef = useRef(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const displayUrl = preview || avatarUrl;
    const hasPhoto = !!(preview || resolveAvatarUrl(avatarUrl));
    const deleteUrl = removeUrl || uploadUrl;

    const onPick = (file) => {
        if (!file) return;
        setErr("");
        if (!ACCEPT.split(",").some((t) => file.type === t.trim() || file.type === "image/jpeg")) {
            const okExt = /\.(png|jpe?g|webp)$/i.test(file.name);
            if (!okExt) {
                setErr("Use PNG, JPG, or WebP only");
                return;
            }
        }
        if (file.size > MAX_BYTES) {
            setErr("Image must be 5MB or smaller");
            return;
        }
        setPreview(URL.createObjectURL(file));
        upload(file);
    };

    const upload = async (file) => {
        setBusy(true);
        setErr("");
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post(uploadUrl, form);
            onUploaded?.(data);
            setPreview(null);
        } catch (e) {
            setErr(extractAxiosError(e, "Upload failed"));
            setPreview(null);
        } finally {
            setBusy(false);
        }
    };

    const removePhoto = async () => {
        if (!hasPhoto || busy || !deleteUrl) return;
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.delete(deleteUrl);
            onUploaded?.(data);
            setPreview(null);
            if (inputRef.current) inputRef.current.value = "";
        } catch (e) {
            setErr(extractAxiosError(e, "Could not remove photo"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center" data-testid="avatar-upload">
            <div className="relative size-20 shrink-0">
                <div className="size-20 overflow-hidden rounded-full border border-white/[0.06] bg-[#262626]">
                    <UserAvatar
                        name={name}
                        avatarUrl={displayUrl}
                        embedded
                        className="size-full text-lg"
                    />
                </div>
                {busy && (
                    <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                        <Loader2 className="h-5 w-5 animate-spin text-white" />
                    </span>
                )}
                {hasPhoto && !busy && (
                    <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute right-0 top-0 flex size-6 translate-x-1/4 -translate-y-1/4 items-center justify-center rounded-full border border-white/15 bg-[#1A1A1A] text-white/70 transition-colors hover:border-rose-500/40 hover:bg-rose-500/15 hover:text-rose-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/35"
                        title="Remove photo"
                        aria-label="Remove profile photo"
                        data-testid="avatar-remove-button"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            <div className="flex-1 text-center sm:text-left">
                <p className="label-eyebrow">{label}</p>
                <p className="mt-1 text-xs text-white/40">PNG, JPG, or WebP · max 5MB</p>
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    data-testid="avatar-file-input"
                    onChange={(e) => onPick(e.target.files?.[0])}
                />
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => inputRef.current?.click()}
                    className="btn-ghost mt-3 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs"
                    data-testid="avatar-upload-button"
                >
                    <Upload className="h-3.5 w-3.5" />
                    {busy ? "Uploading…" : hasPhoto ? "Change photo" : "Upload photo"}
                </button>
                {err && <p className="mt-2 text-xs text-rose-400" data-testid="avatar-upload-error">{err}</p>}
            </div>
        </div>
    );
}
